import type { Comment, CreateCommentInput } from "@adafia/types";
import { useAuth } from "@adafia/ui";
import { Trash2 } from "lucide-react";
import type React from "react";
import { CommentForm } from "./CommentForm";

interface MarginCommentThreadProps {
	blockId: string;
	comments: Comment[];
	isActive: boolean;
	onOpen: () => void;
	onClose: () => void;
	onSubmit: (data: Omit<CreateCommentInput, "nodeId">) => void;
	onCommentDelete?: (commentId: string) => void;
	isLoading: boolean;
}

export const MarginCommentThread: React.FC<MarginCommentThreadProps> = ({
	blockId,
	comments,
	isActive,
	onOpen,
	onClose,
	onSubmit,
	onCommentDelete,
	isLoading,
}) => {
	const { user } = useAuth();
	const isAdmin = user?.role === "admin";

	if (!isActive && comments.length === 0) return null;

	const renderComments = () => (
		<>
			{comments.map((comment) => (
				<div key={comment.id} className="margin-comment">
					<div
						className="comment-meta"
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div
							style={{ display: "flex", alignItems: "baseline", gap: "4px" }}
						>
							<span className="comment-author">{comment.authorName}</span>
							<span className="comment-date">
								{new Date(comment.createdAt || new Date()).toLocaleDateString()}
							</span>
						</div>
						{isAdmin && comment.id && onCommentDelete && (
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation(); // prevent opening thread if clicked on un-active button area
									const commentId = comment.id;
									if (commentId) {
										onCommentDelete(commentId);
									}
								}}
								className="comment-delete-btn"
								title="Delete comment"
								style={{
									background: "none",
									border: "none",
									color: "var(--color-error, #ef4444)",
									cursor: "pointer",
									padding: "2px",
									display: "flex",
									alignItems: "center",
									opacity: 0.6,
									transition: "opacity 0.2s",
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.opacity = "1";
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.opacity = "0.6";
								}}
							>
								<Trash2 size={12} />
							</button>
						)}
					</div>
					<p className="comment-body">{comment.content}</p>
				</div>
			))}
		</>
	);

	return (
		<div className={`margin-thread-container ${isActive ? "active" : ""}`}>
			{comments.length > 0 && !isActive && (
				<button
					type="button"
					className="margin-comments-list interactive-list"
					onClick={onOpen}
				>
					{renderComments()}
				</button>
			)}

			{comments.length > 0 && isActive && (
				<div className="margin-comments-list">{renderComments()}</div>
			)}

			{isActive && (
				<div className="margin-comment-compose">
					<CommentForm
						className="margin-comment-form"
						placeholder="Reply or add a comment..."
						isLoading={isLoading}
						showCancel
						onCancel={onClose}
						onSubmit={(data) => {
							onSubmit({ ...data, blockId });
						}}
					/>
				</div>
			)}
		</div>
	);
};
